import type { TokenStore } from './api';

/**
 * Browser store. Every access is wrapped because localStorage can throw in
 * private windows or when site data is blocked; the app must still render.
 * Not a cookie: the SPA and the API are on different hosts and Safari blocks
 * third-party cookies.
 */
export function createLocalStorageTokenStore(
  key = 'fsv.session',
): TokenStore & { peek(): string | null } {
  const storage = (): Storage | null => {
    try {
      return typeof window !== 'undefined' ? window.localStorage : null;
    } catch {
      return null;
    }
  };
  return {
    peek() {
      try {
        return storage()?.getItem(key) ?? null;
      } catch {
        return null;
      }
    },
    async load() {
      return this.peek();
    },
    async save(token) {
      try {
        storage()?.setItem(key, token);
      } catch {
        /* private mode: the session simply does not survive a reload */
      }
    },
    async clear() {
      try {
        storage()?.removeItem(key);
      } catch {
        /* nothing to clear */
      }
    },
  };
}

/** In-memory store for tests and for the server-side rendering path. */
export function createMemoryTokenStore(initial: string | null = null): TokenStore {
  let value = initial;
  return {
    async load() {
      return value;
    },
    async save(token) {
      value = token;
    },
    async clear() {
      value = null;
    },
  };
}
